// src/components/courses/AttachAITutorDialog.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@mui/material';
import { tutorsApi, coursesApi } from '../../../services/api';
import { useError } from '../../../contexts/ErrorContext';
import FormDialog from '../../common/FormDialog';

const AttachAITutorDialog = ({ open, onClose, course, token }) => {
    const { t } = useTranslation();
    const { showError } = useError();

    // Состояния для данных
    const [aiTutors, setAiTutors] = useState([]);
    const [selectedTutorId, setSelectedTutorId] = useState('');
    const [attachType, setAttachType] = useState('course'); // 'course' или 'lesson'
    const [selectedLessonId, setSelectedLessonId] = useState('');

    // Состояния для загрузки
    const [loading, setLoading] = useState(true);
    const [attaching, setAttaching] = useState(false);

    // Загрузка AI-тьюторов
    useEffect(() => {
        if (open && token) {
            loadAITutors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, token]);

    // Функция загрузки AI-тьюторов
    const loadAITutors = async () => {
        setLoading(true);
        try {
            const response = await tutorsApi.getAITutors('owned', token);
            setAiTutors(response || []);
        } catch (err) {
            
            showError(t('dashboard.courses.error.loading_tutors'));
        } finally {
            setLoading(false);
        }
    };

    // Обработчик изменения выбранного тьютора
    const handleTutorChange = (event) => {
        setSelectedTutorId(event.target.value);
    };

    // Обработчик изменения типа прикрепления
    const handleTypeChange = (event) => {
        setAttachType(event.target.value);
        // Сбрасываем выбранный урок при изменении типа
        if (event.target.value === 'course') {
            setSelectedLessonId('');
        }
    };

    // Обработчик изменения выбранного урока
    const handleLessonChange = (event) => {
        setSelectedLessonId(event.target.value);
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        if (!selectedTutorId) return;

        setAttaching(true);

        try {
            if (attachType === 'course') {
                // Прикрепляем AI-тьютора к курсу
                await coursesApi.attachAssistantToCourse(course.id, selectedTutorId, token);
            } else if (attachType === 'lesson' && selectedLessonId) {
                // Здесь должна быть функция прикрепления к уроку
                // await attachAITutorToLesson(token, selectedLessonId, selectedTutorId, {
                //     onError: (error) => {
                //         showError(error);
                //         throw new Error(error);
                //     }
                // });
                // Поскольку этой функции нет в API, оставляем заглушку
            }

            // Успешное прикрепление
            showError(t('dashboard.courses.tutor_attached_success'), 'success');
            onClose();
        } catch (err) {
            
            showError(t('dashboard.courses.error.attaching_tutor'));
        } finally {
            setAttaching(false);
        }
    };

    // Функция получения списка всех уроков курса
    const getAllLessons = () => {
        if (!course || !course.modules) return [];

        return course.modules.flatMap(module =>
            module.lessons.map(lesson => ({
                id: lesson.id,
                title: lesson.title,
                moduleTitle: module.title
            }))
        );
    };

    // Получаем все уроки курса
    const allLessons = getAllLessons();

    // Проверяем, можно ли отправить форму
    const canSubmit = selectedTutorId && (attachType === 'course' || (attachType === 'lesson' && selectedLessonId));

    // Содержимое диалога
    const dialogContent = loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
        </Box>
    ) : (
        <Box sx={{ '& > *': { mb: 3 } }}>
            <Typography gutterBottom>
                {t('dashboard.courses.attach_tutor_dialog.description', { name: course?.title })}
            </Typography>

            <FormControl fullWidth>
                <InputLabel id="tutor-select-label">
                    {t('dashboard.courses.attach_tutor_dialog.select_tutor')}
                </InputLabel>
                <Select
                    labelId="tutor-select-label"
                    value={selectedTutorId}
                    onChange={handleTutorChange}
                    label={t('dashboard.courses.attach_tutor_dialog.select_tutor')}
                    disabled={attaching}
                >
                    {aiTutors.map(tutor => (
                        <MenuItem key={tutor.id} value={tutor.id}>
                            {tutor.name}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    {t('dashboard.courses.attach_tutor_dialog.tutor_helper')}
                </FormHelperText>
            </FormControl>

            <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                    {t('dashboard.courses.attach_tutor_dialog.attach_to')}
                </Typography>
                <RadioGroup
                    value={attachType}
                    onChange={handleTypeChange}
                >
                    <FormControlLabel
                        value="course"
                        control={<Radio />}
                        label={t('dashboard.courses.attach_tutor_dialog.entire_course')}
                        disabled={attaching}
                    />
                    <FormControlLabel
                        value="lesson"
                        control={<Radio />}
                        label={t('dashboard.courses.attach_tutor_dialog.specific_lesson')}
                        disabled={attaching || allLessons.length === 0}
                    />
                </RadioGroup>
            </FormControl>

            {attachType === 'lesson' && (
                <FormControl fullWidth disabled={attaching}>
                    <InputLabel id="lesson-select-label">
                        {t('dashboard.courses.attach_tutor_dialog.select_lesson')}
                    </InputLabel>
                    <Select
                        labelId="lesson-select-label"
                        value={selectedLessonId}
                        onChange={handleLessonChange}
                        label={t('dashboard.courses.attach_tutor_dialog.select_lesson')}
                    >
                        {allLessons.map(lesson => (
                            <MenuItem key={lesson.id} value={lesson.id}>
                                {lesson.moduleTitle} - {lesson.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );

    return (
        <FormDialog
            open={open}
            onClose={!attaching ? onClose : undefined}
            title={t('dashboard.courses.attach_tutor_dialog.title')}
            submitLabel={attaching ? t('common.attaching') : t('common.attach')}
            cancelLabel={t('common.cancel')}
            onSubmit={handleSubmit}
            isSubmitting={attaching}
            isValid={canSubmit}
            maxWidth="sm"
        >
            {dialogContent}
        </FormDialog>
    );
};

export default AttachAITutorDialog;
